/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useAiContext } from '../providers/ai-provider';
import { X, Brain, ExternalLink, User, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCompact, formatAddress } from '../lib/utils/format';
import { SmartMoneyTrader } from '../types/ai';

interface SmartMoneyPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

const TraderRow: React.FC<{ trader: SmartMoneyTrader }> = ({ trader }) => {
    return (
        <div className="p-3 bg-stone-50 rounded-xl border border-stone-100 hover:bg-white hover:shadow-md transition-all group">
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-white border border-stone-200 flex items-center justify-center text-stone-400 text-xs font-bold">
                        {trader.address.slice(0, 2)}
                    </div>
                    <div>
                        <div className="font-mono text-sm font-bold text-stone-800">{formatAddress(trader.address, 6)}</div>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[9px] font-bold bg-green-100 text-green-700 px-1.5 rounded border border-green-200">Win Rate: {trader.winRate}%</span>
                            {trader.badges.map(b => (
                                <span key={b} className="text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 rounded border border-blue-200">{b}</span>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div className={`text-lg font-bold font-mono ${trader.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trader.pnl >= 0 ? '+' : ''}${formatCompact(trader.pnl)}
                    </div>
                     <div className="text-[9px] text-stone-400 font-mono">Total PnL</div>
                </div>
            </div>
            <div className="flex items-center justify-between text-xs text-stone-500 border-t border-stone-200 mt-2 pt-2 px-1">
                <span className="font-bold uppercase tracking-wide flex items-center gap-1.5">
                    Last Trade: <span className="font-mono text-stone-800">${trader.lastTrade.symbol}</span>
                </span>
                <span className={`font-bold flex items-center gap-1 ${trader.lastTrade.type === 'BUY' ? 'text-green-600' : 'text-red-600'}`}>
                    {trader.lastTrade.type}
                    {trader.lastTrade.type === 'BUY' ? <TrendingUp size={14}/> : <TrendingDown size={14} />}
                </span>
            </div>
        </div>
    );
};

export const SmartMoneyPopup: React.FC<SmartMoneyPopupProps> = ({ isOpen, onClose }) => {
    const { smartMoney, isLoading } = useAiContext();

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div 
                className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col h-full max-h-[80vh] border border-stone-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-900 rounded-full flex items-center justify-center text-nobel-gold">
                            <Brain size={20} />
                        </div>
                        <div>
                            <h3 className="font-serif text-xl font-bold text-stone-900">Smart Money Tracker</h3>
                            <p className="text-xs text-stone-500 font-medium">Live activity from top performing wallets.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full text-stone-400 hover:text-stone-900" title="Close">
                        <X size={20} />
                    </button>
                </div>
                
                {/* List */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-stone-50/50">
                    {isLoading ? (
                        <div className="text-center py-20 text-stone-400">Loading wallet data...</div>
                    ) : (
                        <div className="space-y-3">
                            {smartMoney.map((trader, i) => (
                                <TraderRow key={i} trader={trader} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-white border-t border-stone-100 text-center">
                    <p className="text-[10px] text-stone-400">
                        Data is for informational purposes only. This is not financial advice.
                    </p>
                </div>
            </div>
        </div>
    );
};